// @ts-check
// =============================================================================
// js/screens/duel/targeting.js — the selection → target state machine.
//
// WHAT THIS IS
//   The port of the old client's Click() (:4847). All duel input funnels
//   through click(spot): resource buttons ("Draw", "Crop", "Devour"), hand
//   cards ("Card_<i>"), board tiles ("Board_<y>_<x>"), the two face targets
//   ("Yourself", "Opponent"), plus "Mulligan" and "Leave".
//
// THE STATE MACHINE (kept exactly — this is gameplay-critical)
//   - Draw / Crop        fire immediately (no target step).
//   - Mulligan / Leave   fire immediately (Mulligan only pre-first-action —
//                        the server re-validates anyway).
//   - Click a legal SOURCE (sources[spot])       -> becomes the selection;
//                        legal targets are computed via the shared rules.
//   - Click a legal TARGET while selected        -> compose the string action:
//        selection "Devour"      -> "Devour__<target>"
//        selection "Board_y_x"   -> "Attack__Board_y_x__<target>"
//        selection "Card_i"      -> "Play__Card_i__<target>"
//   - Click the selection again                  -> deselect.
//
//   sources come from GetSources(duel); targets from GetTargets(duel, spot).
//   These are the same modules the server validates with — the client can
//   only ever offer moves the server will accept (UX affordance; the server
//   remains the authority).
// =============================================================================

import { store } from '../../core/store.js';
import { GetSources, GetTargets, isFirstPlayerOpeningTurn } from '../../rules.js';
import { sendAction } from '../../net/socket.js';

/** The current selection state. Screens read it to draw highlights. */
export const targeting = {
	/** @type {string|false} spot code currently selected */
	selection: false,
	/** @type {Record<string, boolean>} legal sources this turn */
	sources: {},
	/** @type {Record<string, boolean>} legal targets for the selection */
	targets: {},
	/** Input lock while animations play (old canClick). */
	locked: false,
};

/** Called after every state change so the scene redraws highlights. */
let onChanged = () => {};

/**
 * Register the scene's refresh callback.
 * @param {() => void} fn
 * @returns {void}
 */
export function setTargetingChanged(fn) {
	onChanged = fn;
}

/** Fired the moment a TICK-COSTING action goes out (bars preview the spend). */
let onActionSent = () => {};

/** Fired when a source is picked whose ONLY enemy reach was the opponent face,
 *  blocked by the opening-turn rule (duel.js shows the explaining tooltip). */
let onFirstTurnBlocked = () => {};

/**
 * Register the "opening-turn face block" handler (duel.js shows the tooltip;
 * pass null to clear on unmount).
 * @param {(() => void)|null} fn
 * @returns {void}
 */
export function setFirstTurnBlockedHandler(fn) {
	onFirstTurnBlocked = fn || (() => {});
}

/**
 * Register the "action sent" listener (duel.js wires the tick-spend preview;
 * pass null to clear on unmount).
 * @param {(() => void)|null} fn
 * @returns {void}
 */
export function setActionSent(fn) {
	onActionSent = fn || (() => {});
}

// -----------------------------------------------------------------------------
// The send->reply input lock. The tick preview makes an action LOOK paid
// instantly, but the game state is still the old one until the server's
// update lands — a second click in that window would fire against stale
// state. So: sending locks input; the confirming update unlocks it (via the
// animation queue draining, or directly on a no-animation commit).
// -----------------------------------------------------------------------------

/** Failsafe: a dropped/invalid action must never wedge the input. */
let serverWaitTimer = /** @type {ReturnType<typeof setTimeout>|null} */ (null);

/** Lock input until the server answers (armed with a 4s failsafe). */
function lockUntilServerReplies() {
	targeting.locked = true;
	if (serverWaitTimer) clearTimeout(serverWaitTimer);
	serverWaitTimer = setTimeout(() => {
		serverWaitTimer = null;
		targeting.locked = false;
		onChanged();
	}, 4000);
}

/**
 * The server's update arrived: disarm the failsafe. The animation flow now
 * owns the lock (animateAction holds it; the queue draining releases it).
 * @returns {void}
 */
export function serverReplied() {
	if (serverWaitTimer) {
		clearTimeout(serverWaitTimer);
		serverWaitTimer = null;
	}
}

/**
 * Fully release the input lock (queue drained / direct commit).
 * @returns {void}
 */
export function unlockInput() {
	serverReplied();
	targeting.locked = false;
}

/**
 * Clear selection state (new duel update arrived, or leaving the duel).
 * @param {boolean} [recompute]  Also recompute sources for the new state.
 * @returns {void}
 */
export function resetTargeting(recompute = true) {
	targeting.selection = false;
	targeting.targets = {};
	const duel = store.duel;
	// Only OUR turn has sources; spectators/waiting players get none.
	if (recompute && duel && !duel.spectating && typeof duel.player1.hand !== 'number' && duel.player1.tick) {
		try {
			targeting.sources = GetSources(duel) || {};
		} catch (error) {
			console.error('GetSources failed:', error);
			targeting.sources = {};
		}
	} else {
		targeting.sources = {};
	}
}

/**
 * Handle a click on any duel spot. Port of Click() — see the header.
 * @param {string} spot
 * @returns {void}
 */
export function click(spot) {
	if (targeting.locked) return;
	const duel = store.duel;
	if (!duel) return;
	if (duel.spectating) {
		// A spectator's ONLY action is Leave (stop watching) — and it MUST go
		// through: without this dispatch the server never removes them from
		// duel.spectators, and the next Duel Update push shunts them right
		// back into the game they just left.
		if (spot === 'Leave') sendAction('Leave');
		return;
	}

	if (spot === 'Leave') {
		sendAction('Leave');
		return;
	}
	if (spot === 'Mulligan') {
		sendAction('Mulligan');
		lockUntilServerReplies();
		return;
	}
	// Everything below requires it to be our turn.
	if (duel.player1.tick === 0) return;

	// Toggling the current selection off.
	if (spot === targeting.selection) {
		targeting.selection = false;
		targeting.targets = {};
		onChanged();
		return;
	}

	// No-target actions fire straight away.
	if (spot === 'Draw' || spot === 'Crop') {
		sendAction(spot);
		onActionSent(); // every action costs a tick — preview the spend
		lockUntilServerReplies();
		targeting.selection = false;
		targeting.targets = {};
		onChanged();
		return;
	}

	// A legal target while something is selected -> compose + send the action.
	if (targeting.selection && targeting.targets && targeting.targets[spot]) {
		let action;
		if (targeting.selection === 'Devour') {
			action = 'Devour__' + spot;
		} else if (targeting.selection.includes('Board')) {
			action = 'Attack__' + targeting.selection + '__' + spot;
		} else {
			action = 'Play__' + targeting.selection + '__' + spot;
		}
		sendAction(action);
		onActionSent(); // every action costs a tick — preview the spend
		lockUntilServerReplies();
		targeting.selection = false;
		targeting.targets = {};
		onChanged();
		return;
	}

	// A legal source -> select it and compute its targets.
	if (targeting.sources[spot]) {
		targeting.selection = spot;
		try {
			// GetTargets accepts the spot code directly (it resolves "Card_<i>"
			// to the hand card itself).
			targeting.targets = GetTargets(duel, spot) || {};
		} catch (error) {
			console.error('GetTargets failed:', error);
			targeting.targets = {};
		}
		maybeFirstTurnNotice(duel, spot);
		onChanged();
	}
}

/**
 * On the first player's opening turn, if the just-picked source could normally
 * hit the opponent FACE but the rule blocks it AND it has no enemy CREATURE to
 * hit instead, tell the scene to explain why. (If a creature is targetable, the
 * player has a real play — no nag; the rule is face-only.)
 * @param {object} duel
 * @param {string} spot
 * @returns {void}
 */
function maybeFirstTurnNotice(duel, spot) {
	try {
		if (!isFirstPlayerOpeningTurn(duel)) return;
		// Would the face have been a target without the opening-turn rule?
		const unrestricted = GetTargets(duel, spot, { ignoreFirstTurn: true }) || {};
		if (unrestricted.Opponent !== true) return; // it never could hit the face
		const hasEnemyCreature = Object.keys(targeting.targets).some(
			(key) => /^Board_[01]_/.test(key) && targeting.targets[key],
		);
		if (hasEnemyCreature) return; // there's a creature to hit — the rule isn't in the way
		onFirstTurnBlocked();
	} catch (error) {
		console.error('first-turn notice check failed:', error);
	}
}

/**
 * What highlight (if any) a spot gets right now.
 * Same precedence as the old Highlight(): targeted > selected > pickable.
 * NOTE: renderers no longer DRAW the 'pickable' state (no cyan) — what you
 * can act on stays full-color while everything else grays (see grayFor) —
 * but it still gates clicks (e.g. the portrait only accepts targeted taps).
 * @param {string} spot
 * @returns {'targeted'|'selected'|'pickable'|null}
 */
export function highlightFor(spot) {
	if (targeting.targets?.[spot]) return 'targeted';
	if (targeting.selection === spot) return 'selected';
	if (targeting.sources?.[spot]) return 'pickable';
	return null;
}

/**
 * Should a spot render 50% grayscale right now?
 *
 * The inverse cue that replaced the pickable-cyan rings: while YOU have
 * agency (it's your turn — sources exist), everything you can't currently
 * act on drops to half grayscale; sources, the selection, and its targets
 * keep full color. With no agency (opponent's turn, spectating, waiting)
 * nothing grays — the board just looks normal while you watch.
 * @param {string} spot
 * @returns {boolean}
 */
export function grayFor(spot) {
	for (const key in targeting.sources) {
		if (targeting.sources[key]) {
			return !highlightFor(spot);
		}
	}
	return false;
}
